import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, Receipt, TrendingUp, Settings as SettingsIcon, LogOut, FlaskRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const NAV = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/services", key: "services", icon: Sparkles },
  { to: "/materials", key: "materials", icon: FlaskRound },
  { to: "/fixed-costs", key: "fixedCosts", icon: Receipt },
  { to: "/simulator", key: "simulator", icon: TrendingUp },
  { to: "/settings", key: "settings", icon: SettingsIcon },
];

export function Layout({ children }) {
  const { t, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-brand-ivory text-brand-anthracite pb-24 md:pb-0">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-brand-line bg-white/80 backdrop-blur-md p-6 fixed h-full z-30">
        <Wordmark />
        <nav className="mt-10 flex flex-col gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.key}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                  isActive ? "bg-brand-blush/40 text-brand-anthracite" : "text-brand-taupe hover:bg-brand-sand"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={handleLogout}
          data-testid="logout-button-desktop"
          className="mt-auto flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-brand-taupe transition-colors hover:bg-brand-sand"
        >
          <LogOut className="h-5 w-5" />
          {t("auth.logout")}
        </button>
      </aside>

      {/* Mobile top header */}
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between border-b border-brand-line bg-brand-ivory/95 backdrop-blur-md px-4 py-3">
        <Wordmark compact />
        <LanguageSwitcher testId="language-switcher-trigger-mobile" />
      </header>

      {/* Desktop top-right language */}
      <div className="hidden md:flex fixed top-5 right-6 z-40">
        <LanguageSwitcher testId="language-switcher-trigger-desktop" />
      </div>

      <main className="md:ml-64 p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto space-y-6 md:space-y-8">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around border-t border-brand-line bg-white/95 backdrop-blur-md px-1 h-16">
        {NAV.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.end}
            data-testid={`mobilenav-${item.key}`}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center justify-center gap-1 py-1 text-[10px] font-medium transition-colors ${
                isActive ? "text-brand-anthracite" : "text-brand-taupe"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${isActive ? "bg-brand-blush/50" : ""}`}>
                  <item.icon className="h-5 w-5" />
                </span>
                {t(`nav.${item.key}`)}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
